import { TransactionRowResult } from "@/models/transaction";
import { formatDateTime, formatAmount, formatDate } from "@/util";
import { Box, Grid, List, ListItem } from "@mui/material";
import React, { Dispatch, FC, SetStateAction, useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import StatusActive from "../../assets/icons/status-active.svg";
import StatusInactive from "../../assets/icons/status-inactive.svg";

interface ITransactionsGrid {
    sortBy: Function;
    transactions: TransactionRowResult[];
    isSearch: boolean;
    fetchMore: Function;
    hasNext: boolean;
    setSelectedTransaction: Dispatch<SetStateAction<TransactionRowResult | null>>;
}

const AllTransactionsGrid: FC<ITransactionsGrid> = (props: ITransactionsGrid) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [filterOrderBy, setFilterByOrderBy] = useState<string>("Id");
    const [filterAsc, setFilterAsc] = useState<boolean>(true);
    const gridEl = useRef() as React.MutableRefObject<HTMLInputElement>;
    const navigate = useNavigate();

    const handleSelectedTransaction = (transaction: TransactionRowResult) => {
        props.setSelectedTransaction(transaction);
    };

    /* infinite scroll */
    const scrollListener = useCallback(() => {
        let bottom = gridEl.current.scrollHeight - gridEl.current.clientHeight;
        if (gridEl.current.scrollTop > 0 && gridEl.current.scrollTop >= bottom && props.hasNext) {
            props.fetchMore();
        }
    }, [gridEl, props]);
    useLayoutEffect(() => {
        const gridRef = gridEl.current;
        gridRef.addEventListener("scroll", scrollListener);
        return () => {
            gridRef.removeEventListener("scroll", scrollListener);
        };
    }, [scrollListener]);
    /* infinite scroll */

    function getArrowClass(sort: string) {
        if (sort === filterOrderBy) {
            if (filterAsc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }
    return (
        <Grid
            sx={{
                width: "100%",
                position: "relative",
            }}
        >
            <Grid className="all-transaction transaction-search-grid" ref={gridEl}>
                {props.transactions.length > 0 && (
                    <List className="transaction-search-list">
                        <ListItem className="transaction-search-list-header">
                            <Grid className="transaction-search-grid-header">
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionEmployeeNumber")}
                                </Grid>
                                <Grid item width="70%">
                                    {t("transaction.TransactionEmployeeName")}
                                </Grid>
                                <Grid item width="70%">
                                    {t("transaction.TransactionEmployeeStatus")}
                                </Grid>
                                <Grid item width="70%" className="text-align-right">
                                    {t("digitalWallet.ExpirationDate")}
                                </Grid>
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionNumber")}
                                </Grid>
                                <Grid item width="70%">
                                    {t("transaction.TransactionType")}
                                </Grid>
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionDate")}
                                </Grid>
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionAmount")}
                                </Grid>
                                <Grid item width="70%">
                                    {t("interfaces.user")}
                                </Grid>
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionConnectedOrder")}
                                </Grid>
                            </Grid>
                        </ListItem>
                        {props.transactions.map((row) => (
                            <ListItem
                                className="transaction-search-list-item"
                                key={row.id}
                                onClick={() => handleSelectedTransaction(row)}
                            >
                                <Grid className="transaction-search-grid-body">
                                    <Grid item width="70%" className="text-align-right">
                                        {row.employeeNumber}
                                    </Grid>
                                    <Grid item width="70%">
                                        {row.employeeName}
                                    </Grid>
                                    <Grid item width="90%">
                                        <Box sx={{ display: "flex" }}>
                                            <Box
                                                component="img"
                                                sx={{
                                                    height: "8px",
                                                    width: "8px",
                                                    marginTop: "6px",
                                                }}
                                                alt={t("general.Active")}
                                                src={row.employeeStatus ? StatusActive : StatusInactive}
                                            />
                                            <Box sx={{ padding: "0 5px", fontSize: "16px" }}>
                                                {row.employeeStatus ? t("general.Active") : t("general.Inactive")}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        {row.walletExpiryDate}
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        {row.id}
                                    </Grid>
                                    <Grid item width="90%">
                                        {t(`transaction.${row.transactionType}`)}
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        {formatDateTime(row.transactionDate)}
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        <div className={`transaction-amount ${row.transactionType}`}>
                                            {formatAmount(row.transactionAmount, row.currencyId)}
                                        </div>
                                    </Grid>
                                    <Grid item width="70%">
                                        {row.username}
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        {row.connectedOrderId != null ? (
                                            <div
                                                className="link-element table-number-format-value"
                                                onClick={() =>
                                                    navigate(
                                                        `/digital-wallet/list/${id}/transactions/${row.connectedOrderId}`,
                                                        { state: { docketNumber: row.docket } }
                                                    )
                                                }
                                            >
                                                {row.docket}
                                            </div>
                                        ) : (
                                            ""
                                        )}
                                    </Grid>
                                </Grid>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>
        </Grid>
    );
};

export default AllTransactionsGrid;
